import { Quote } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { monthLabel, photoUrl } from "@/lib/reviews";
import { Stars } from "./Stars";

const SMALL_COLS = { 1: "grid-cols-1 md:grid-cols-2", 2: "grid-cols-2", 3: "grid-cols-2 md:grid-cols-3", 4: "grid-cols-2 md:grid-cols-4" };

export const Byline = ({ r, light }) => (
  <p className={`text-xs ${light ? "text-slate-300" : "text-slate-500"}`}>
    <span className={`font-semibold ${light ? "text-white" : "text-navy"}`}>{r.name}</span>
    {r.location && ` · ${r.location}`}{r.trip && ` · ${r.trip}`}{r.travel_month && ` · ${monthLabel(r.travel_month)}`}
  </p>
);

export const ReviewCard = ({ r, onPhoto, clamp = true, className = "" }) => (
  <article className={`rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col ${className}`} data-testid={`guest-review-${r.id}`}>
    {r.photo_ids[0] && (
      <button type="button" onClick={() => onPhoto?.({ pid: r.photo_ids[0], r })} className="block aspect-[16/10] overflow-hidden bg-slate-100" data-testid={`guest-review-photo-${r.id}`}>
        <img src={photoUrl(r.photo_ids[0])} alt={`Trip photo by ${r.name}`} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
      </button>
    )}
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-center justify-between"><Stars value={r.rating} /><Quote className="h-6 w-6 text-sky-100" /></div>
      {r.title && <p className="mt-4 font-display text-xl text-navy font-semibold">{r.title}</p>}
      <p className={`mt-3 text-sm text-slate-600 leading-relaxed flex-1 whitespace-pre-line ${clamp ? "line-clamp-6" : ""}`}>{r.text}</p>
      <div className="mt-5 pt-4 border-t border-slate-100"><Byline r={r} /></div>
    </div>
  </article>
);

export const Lightbox = ({ item, onClose }) => (
  <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-4xl p-0 overflow-hidden bg-navy border-0" data-testid="guest-lightbox">
      <DialogTitle className="sr-only">Guest photo</DialogTitle>
      {item && (
        <div>
          <img src={photoUrl(item.pid)} alt={`Trip photo by ${item.r.name}`} className="w-full max-h-[70vh] object-contain bg-black" />
          <div className="p-5 space-y-2"><Stars value={item.r.rating} /><p className="text-sm text-slate-200 line-clamp-3">{item.r.text}</p><Byline r={item.r} light /></div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const PhotoWall = ({ reviews, onOpen, max = 7 }) => {
  const photos = reviews.flatMap((r) => r.photo_ids.map((pid) => ({ pid, r }))).slice(0, max);
  if (!photos.length) return null;
  const big = photos.length >= 5;
  return (
    <div className={`grid gap-3 auto-rows-[160px] md:auto-rows-[200px] ${big ? "grid-cols-2 md:grid-cols-4" : SMALL_COLS[photos.length]}`} data-testid="guest-photo-wall">
      {photos.map((p, i) => (
        <button key={p.pid} onClick={() => onOpen(p)} data-testid={`guest-photo-${i}`}
          className={`group relative overflow-hidden rounded-2xl bg-slate-100 ${i === 0 && big ? "col-span-2 row-span-2" : ""} ${big ? "" : "md:row-span-2"}`}>
          <img src={photoUrl(p.pid)} alt={`Trip photo by ${p.r.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <span className="absolute inset-x-0 bottom-0 p-3 text-left text-xs font-semibold text-white bg-gradient-to-t from-[#0A192F]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">{p.r.name}</span>
        </button>
      ))}
    </div>
  );
};

export const RatingSummary = ({ data, testId = "guest-stories-summary" }) => (
  <div className="flex items-center gap-3" data-testid={testId}>
    <Stars value={data.average} size="h-5 w-5" />
    <span className="font-mono text-lg font-semibold text-navy">{data.average.toFixed(1)}</span>
    <span className="text-sm text-slate-500">from {data.count} review{data.count === 1 ? "" : "s"}</span>
  </div>
);
